import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';
import { MiSpinner, WizOutlineButton } from '../../components/wizard-ui';
import { AdCheckItem } from '../../campaign-wizard.types';
import { LOCATION_LABEL, locationMissing } from './job-gate.logic';
import LocationQuestion from './location-question';
import {
  AdLevelMeta,
  AdStatus,
  adLevelMeta,
  hasConcreteAdd,
  isAddedToExtras,
  missingItems,
  StoredAdCheck,
} from './job-logic';

const LEVEL_ICONS = {
  error_outline: ErrorOutlineIcon,
  info: InfoIcon,
  check_circle: CheckCircleIcon,
  edit_note: EditNoteIcon,
} as const;

interface Props {
  status: AdStatus;
  adCheck: StoredAdCheck;
  /** Inhalt des Ergänzungsfelds: schon übernommene Vorschläge zeigen „Ergänzt“. */
  extras: string;
  canCheck: boolean;
  error: React.ReactNode;
  /** Fehlende Punkte hervorheben (nach „Anzeige ergänzen“ im Popup). */
  highlightMissing?: boolean;
  onCheck: () => void;
  onFixAll: () => void;
  onFixOne: (item: AdCheckItem) => void;
  /** Antwort auf die Pflichtfrage nach dem Ort. */
  onLocation: (city: string, remote: boolean) => void;
  /** Zählt hoch, wenn die Ortsfrage Fokus bekommen soll. */
  locationFocusTick: number;
}

/** Anzeigenprüfung: Button, Ladezustand und Ergebnis-Karte (Design: `adIdle`/`adChecking`/`adShow`). */
const AdCheckPanel: React.FC<Props> = ({
  status,
  adCheck,
  extras,
  canCheck,
  error,
  highlightMissing,
  onCheck,
  onFixAll,
  onFixOne,
  onLocation,
  locationFocusTick,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const errorLine = error ? <div className={classes.error}>{error}</div> : null;

  if (status === 'idle') {
    return (
      <>
        <div>
          <WizOutlineButton
            onClick={onCheck}
            disabled={!canCheck}
            icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          >
            {t('wizJobCheckButton')}
          </WizOutlineButton>
        </div>
        {errorLine}
      </>
    );
  }

  if (status === 'checking') {
    return (
      <div className={classes.checking}>
        <MiSpinner size={20} />
        {t('wizJobChecking')}
      </div>
    );
  }

  if (!adCheck) return null;
  const stale = status === 'stale';
  const meta: AdLevelMeta = adLevelMeta(adCheck.level);
  const LevelIcon = LEVEL_ICONS[meta.icon];
  const hasMissing =
    missingItems(adCheck.items).some(i => !isAddedToExtras(extras, i)) && status === 'done';
  const title = meta.titleKey ? t(meta.titleKey) : '';
  const desc = adCheck.summary || (meta.descKey ? t(meta.descKey) : '');

  return (
    <>
      <div className={classes.result}>
        <div
          className={classes.head}
          style={{ background: stale ? wz.page : meta.bg, color: stale ? wz.muted : meta.fg }}
        >
          <LevelIcon sx={{ fontSize: 20 }} />
          <div className={classes.headText}>
            <div className={classes.headTitle}>
              {t('wizJobAdTitle', { title, score: adCheck.score, total: adCheck.total })}
            </div>
            <div className={classes.headDesc}>{desc}</div>
          </div>
          {hasMissing ? (
            <button type="button" className={classes.fixAll} onClick={onFixAll}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
              {t('wizJobFixAll')}
            </button>
          ) : null}
          {stale ? (
            <button type="button" className={classes.stale} onClick={onCheck}>
              <RefreshIcon sx={{ fontSize: 16 }} />
              {t('wizJobStale')}
            </button>
          ) : null}
        </div>
        <div className={classes.items}>
          {adCheck.items.map(item => (
            <div
              key={item.label}
              className={classes.item}
              style={
                highlightMissing && !item.ok
                  ? { background: wz.redBg, boxShadow: `inset 3px 0 0 ${wz.redFg}` }
                  : undefined
              }
            >
              {item.ok ? (
                <CheckIcon sx={{ fontSize: 18, color: wz.success }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 18, color: '#ED6C02' }} />
              )}
              <div className={classes.itemLabel} style={{ color: item.ok ? wz.text : wz.textDim }}>
                {item.label}
              </div>
              <div className={classes.itemComment}>
                {item.comment || (item.ok ? t('wizJobCheckPresent') : t('wizJobCheckMissing'))}
              </div>
              {!item.ok && item.label !== LOCATION_LABEL ? (
                isAddedToExtras(extras, item) ? (
                  <span className={classes.added}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                    {t('wizJobAdded')}
                  </span>
                ) : stale ? null : (
                  <button type="button" className={classes.addOne} onClick={() => onFixOne(item)}>
                    {/* Konkrete KI-Ergänzung: „Ergänzen“; sonst nur ein Leitsatz zum Selbst-Ausfüllen. */}
                    {hasConcreteAdd(item) ? t('wizJobAddOne') : t('wizJobFillIn')}
                  </button>
                )
              ) : null}
            </div>
          ))}
          {status === 'done' && locationMissing(adCheck) ? (
            <LocationQuestion
              focusTick={locationFocusTick}
              invalid={highlightMissing}
              onSubmit={onLocation}
            />
          ) : null}
          <div className={classes.source}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: wz.primary }} />
            {adCheck.source === 'ai' ? t('wizJobSourceAi') : t('wizJobSourceHeuristic')}
          </div>
        </div>
      </div>
      {errorLine}
    </>
  );
};

const useStyles = makeStyles()({
  checking: {
    border: `1px solid ${wz.border}`,
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    color: wz.text555,
  },
  result: { border: `1px solid ${wz.border}`, borderRadius: 10, overflow: 'hidden', marginTop: 4 },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' },
  headText: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  headTitle: { fontSize: 14, fontWeight: 700 },
  headDesc: { fontSize: 13, color: wz.textDim },
  fixAll: {
    fontSize: 13,
    color: wz.primary,
    background: '#FFF',
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: 'none',
    fontFamily: 'inherit',
  },
  stale: {
    fontSize: 13,
    color: '#FFF',
    background: wz.gradient,
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: 'none',
    fontFamily: 'inherit',
  },
  items: { display: 'flex', flexDirection: 'column', padding: '6px 14px' },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
    fontSize: 14,
    borderBottom: `1px solid ${wz.page}`,
  },
  itemLabel: { width: 180, flexShrink: 0, fontWeight: 500 },
  itemComment: { flex: 1, minWidth: 0, fontSize: 13, color: wz.muted },
  addOne: {
    fontSize: 13,
    color: wz.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  },
  added: {
    fontSize: 13,
    color: wz.success,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
  },
  source: {
    fontSize: 12,
    color: wz.muted,
    padding: '8px 0 4px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  error: { fontSize: 13, color: wz.redFg },
});

export default AdCheckPanel;
